<template>
  <el-card shadow="never" class="activity-feed" :body-style="{ padding: '14px' }">
    <template #header>
      <div class="feed-header">
        <div class="feed-title">
          <el-icon color="#f56c6c"><VideoPlay /></el-icon>
          <span>Лента активности</span>
          <el-tag size="small" type="danger" effect="dark">LIVE</el-tag>
        </div>
        <el-button
          size="small"
          :icon="Refresh"
          :loading="postsStore.loading"
          @click="refresh"
        >
          Обновить
        </el-button>
      </div>
    </template>

    <el-empty v-if="postsStore.recentPosts.length === 0 && !postsStore.loading" description="Нет недавней активности" />

    <el-skeleton v-else-if="postsStore.loading" :rows="5" animated />

    <el-timeline v-else
>
      <el-timeline-item
        v-for="post in postsStore.recentPosts.slice(0, 10)"
        :key="post.id"
        :type="timelineType(post.status)"
        :timestamp="formatTime(post.scraped_at)"
      >
        <div class="feed-item">
          <div class="feed-item-header">
            <el-tag size="small" :type="statusType(post.status)" effect="plain">
              {{ statusEmoji(post.status) }} {{ statusLabel(post.status) }}
            </el-tag>
            <el-tag v-if="post.category" size="small" :type="categoryType(post.category)" effect="light">
              {{ post.category }}
            </el-tag>
            <span class="feed-source">[{{ post.source }}] {{ post.source_id }}</span>
          </div>
          <div class="feed-content">
            <MarkdownRenderer :text="truncate(post.content, 200)" />
          </div>
          <div v-if="post.confidence" class="feed-confidence">
            Уверенность: {{ (post.confidence * 100).toFixed(0) }}%
          </div>
        </div>
      </el-timeline-item>
    </el-timeline>
  </el-card>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { Refresh } from '@element-plus/icons-vue'
import { usePostsStore } from '@/stores/posts'
import MarkdownRenderer from '@/components/MarkdownRenderer.vue'
import { formatAppDateTime } from '@/config/timezone'

const postsStore = usePostsStore()
let pollInterval: ReturnType<typeof setInterval> | null = null

function refresh() {
  postsStore.loadRecentPosts()
}

function formatTime(dateStr: string | null): string {
  return formatAppDateTime(dateStr, {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  }, 'Неизвестно')
}

function truncate(text: string, max: number): string {
  if (!text) return ''
  return text.length > max ? text.slice(0, max) + '...' : text
}

function timelineType(status: string): 'primary' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status?.toLowerCase()) {
    case 'new': return 'info'
    case 'flagged': return 'warning'
    case 'validated': return 'success'
    case 'archived': return 'primary'
    case 'in_report': return 'primary'
    default: return 'info'
  }
}

function statusType(status: string): 'success' | 'warning' | 'info' | 'danger' {
  switch (status?.toLowerCase()) {
    case 'flagged': return 'warning'
    case 'validated': return 'success'
    case 'archived': return 'info'
    case 'new': return 'info'
    default: return 'info'
  }
}

function statusEmoji(status: string): string {
  switch (status?.toLowerCase()) {
    case 'new': return '⬜'
    case 'flagged': return '🚩'
    case 'validated': return '✅'
    case 'in_report': return '📄'
    case 'archived': return '🗑️'
    default: return '⬜'
  }
}

function statusLabel(status: string): string {
  switch (status?.toLowerCase()) {
    case 'new': return 'Новый'
    case 'flagged': return 'Отмечен'
    case 'validated': return 'Подтв.'
    case 'in_report': return 'В отчёте'
    case 'archived': return 'Архив'
    default: return status
  }
}

function categoryType(category: string): '' | 'success' | 'warning' | 'danger' | 'primary' | 'info' {
  switch (category?.toLowerCase()) {
    case 'medical': return 'primary'
    case 'payment': return 'success'
    case 'abuse': return 'danger'
    case 'conditions': return 'warning'
    default: return 'info'
  }
}

onMounted(() => {
  postsStore.loadRecentPosts()
  pollInterval = setInterval(() => postsStore.loadRecentPosts(), 10000)
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<style scoped>
.activity-feed {
  max-height: 480px;
  overflow-y: auto;
}

.feed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feed-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--app-text-primary);
}

.feed-item {
  padding: 4px 0;
}

.feed-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.feed-source {
  font-size: 13px;
  color: var(--app-text-secondary);
  font-weight: 600;
}

.feed-content {
  font-size: 15px;
  color: var(--app-text-regular);
  line-height: 1.5;
  max-height: 80px;
  overflow: hidden;
}

.feed-content :deep(.markdown-body) {
  font-size: 14px;
}

.feed-content :deep(.markdown-body p) {
  margin: 0 0 4px 0;
}

.feed-content :deep(.markdown-body p:last-child) {
  margin-bottom: 0;
}

.feed-content :deep(.markdown-body strong) {
  font-weight: 700;
}

.feed-content :deep(.markdown-body a) {
  color: #409eff;
}

.feed-confidence {
  font-size: 13px;
  color: var(--app-text-secondary);
  margin-top: 2px;
  font-weight: 600;
}
</style>
